import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  RenderTask,
} from "pdfjs-dist";

export type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask };

const MAX_RENDER_DPR = 2;

/**
 * Sanity's CDN rejects cross-origin fetches of file assets from origins
 * outside the project CORS allowlist (and Vercel preview URLs can never be
 * allowlisted), so PDFs are always fetched through the same-origin proxy.
 */
export function getProxiedPdfUrl(pdfUrl: string): string {
  return `/api/pdf-proxy?url=${encodeURIComponent(pdfUrl)}`;
}

/**
 * Creates the pdfjs loading task and returns it immediately (before the
 * document resolves) so the caller can `destroy()` it on unmount and cancel
 * an in-flight load. Await `.promise` on the returned task for the document.
 */
export async function createPdfLoadingTask(
  pdfUrl: string,
): Promise<PDFDocumentLoadingTask> {
  const pdfjs = await loadPdfjs();
  return pdfjs.getDocument({ url: getProxiedPdfUrl(pdfUrl) });
}

/**
 * The legacy build, not the default: the standard v6 build calls
 * Math.sumPrecise (and Promise.withResolvers) bare, which corrupts glyph
 * widths on browsers without them ("refined" → "refned"). Legacy ships the
 * polyfills in both the main-thread lib and the worker.
 */
export async function loadPdfjs() {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString();
  return pdfjs;
}

export async function getPageAspectRatio(
  doc: PDFDocumentProxy,
  pageNumber = 1,
): Promise<number> {
  const page = await doc.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 1 });
  return viewport.width / viewport.height;
}

export interface PageRender {
  canvas: HTMLCanvasElement;
  task: RenderTask;
}

export async function startPageRender(
  doc: PDFDocumentProxy,
  pageNumber: number,
  width: number,
): Promise<PageRender | null> {
  const page = await doc.getPage(pageNumber);
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_RENDER_DPR);
  const baseViewport = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({
    scale: (width / baseViewport.width) * dpr,
  });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  const context = canvas.getContext("2d");
  if (!context) return null;
  const task = page.render({ canvas, canvasContext: context, viewport });
  return { canvas, task };
}

export function isRenderCancellation(error: unknown): boolean {
  return (error as Error | null)?.name === "RenderingCancelledException";
}

/** A rendered pdf.js text layer, kept so the host can rescale/cancel it. */
export interface TextLayerHandle {
  /** Rescale to a new CSS display scale (container var + span metrics). */
  setScale: (scale: number) => void;
  cancel: () => void;
}

/**
 * Renders the page's selectable/AT-readable text into `container` (which
 * must carry the `.textLayer` class from globals.css). Positions are
 * percentages and font sizes flow through `--total-scale-factor`, so after
 * render only the scale var needs updating when the display width changes.
 */
export async function buildTextLayer(
  doc: PDFDocumentProxy,
  pageNumber: number,
  container: HTMLElement,
  scale: number,
): Promise<TextLayerHandle> {
  const pdfjs = await loadPdfjs();
  const page = await doc.getPage(pageNumber);
  const applyScale = (value: number) => {
    container.style.setProperty("--scale-factor", String(value));
    container.style.setProperty("--total-scale-factor", String(value));
  };
  applyScale(scale);
  const layer = new pdfjs.TextLayer({
    textContentSource: page.streamTextContent(),
    container,
    viewport: page.getViewport({ scale }),
  });
  await layer.render();
  return {
    setScale: (value: number) => {
      applyScale(value);
      layer.update({ viewport: page.getViewport({ scale: value }) });
    },
    cancel: () => layer.cancel(),
  };
}

interface LinkAnnotation {
  subtype: string;
  rect: [number, number, number, number];
  url?: string;
  dest?: string | unknown[];
}

/**
 * Builds percent-positioned `<a>` overlays for the page's Link annotations
 * into `container` (`.pdfLinkLayer`). Percentages make the overlay
 * scale-independent — no rebuild on zoom/resize. Internal destinations are
 * resolved to a page number up front so click handlers stay synchronous.
 */
export async function buildLinkOverlay(
  doc: PDFDocumentProxy,
  pageNumber: number,
  container: HTMLElement,
  options: {
    onInternalLink: (targetPage: number) => void;
    internalLabel: (targetPage: number) => string;
  },
): Promise<void> {
  const page = await doc.getPage(pageNumber);
  const annotations = (await page.getAnnotations({
    intent: "display",
  })) as LinkAnnotation[];
  const viewport = page.getViewport({ scale: 1 });
  const fragment = document.createDocumentFragment();

  for (const annotation of annotations) {
    if (annotation.subtype !== "Link") continue;
    if (!annotation.url && !annotation.dest) continue;

    const [x1, y1] = viewport.convertToViewportPoint(
      annotation.rect[0],
      annotation.rect[1],
    ) as [number, number];
    const [x2, y2] = viewport.convertToViewportPoint(
      annotation.rect[2],
      annotation.rect[3],
    ) as [number, number];
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);

    const link = document.createElement("a");
    link.style.left = `${((left / viewport.width) * 100).toFixed(2)}%`;
    link.style.top = `${((top / viewport.height) * 100).toFixed(2)}%`;
    link.style.width = `${((width / viewport.width) * 100).toFixed(2)}%`;
    link.style.height = `${((height / viewport.height) * 100).toFixed(2)}%`;

    if (annotation.url) {
      link.href = annotation.url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.setAttribute("aria-label", annotation.url);
    } else {
      const dest =
        typeof annotation.dest === "string"
          ? await doc.getDestination(annotation.dest)
          : annotation.dest;
      const ref = Array.isArray(dest) ? dest[0] : null;
      if (!ref || typeof ref !== "object") continue;
      let targetPage: number;
      try {
        targetPage =
          (await doc.getPageIndex(
            ref as Parameters<typeof doc.getPageIndex>[0],
          )) + 1;
      } catch {
        continue;
      }
      link.href = "#";
      link.setAttribute("aria-label", options.internalLabel(targetPage));
      link.addEventListener("click", (event) => {
        event.preventDefault();
        options.onInternalLink(targetPage);
      });
    }
    fragment.append(link);
  }

  container.replaceChildren(fragment);
}

/** Base (scale 1) CSS size of a page, for computing display scale. */
export async function getPageBaseWidth(
  doc: PDFDocumentProxy,
  pageNumber: number,
): Promise<number> {
  const page = await doc.getPage(pageNumber);
  return page.getViewport({ scale: 1 }).width;
}
